import { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Megaphone, X, MessageCircle } from 'lucide-react';

const DISMISS_KEY_PREFIX = 'op-signup-nudge-dismissed-';

/**
 * Phase 323c — Weekly "Re-share your signup QR" Dashboard Nudge
 * --------------------------------------------------------------
 * Surfaces a tiny amber banner on the Owner Dashboard ONLY when:
 *   1. Zero new students in the trailing 7 days, AND
 *   2. Platform WhatsApp number is configured, AND
 *   3. The owner hasn't already dismissed this week's nudge.
 *
 * Dismissal is week-scoped (ISO week tag) — the nudge auto-re-surfaces
 * next Monday so we don't burn quota nagging.
 */
export default function SignupNudgeBanner() {
  const [data, setData] = useState(null);
  const [waLink, setWaLink] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  const load = useCallback(async () => {
    try {
      const [nudge, qr] = await Promise.all([
        api.get('/dashboard/signup-nudge'),
        api.get('/academy/self-signup-qr?role=student').catch(() => ({ data: null })),
      ]);
      setData(nudge.data);
      setWaLink(qr.data?.wa_link || null);
      // Check dismissal for this week.
      const tag = nudge.data?.week_tag;
      if (tag && typeof window !== 'undefined') {
        const stored = window.localStorage.getItem(DISMISS_KEY_PREFIX + tag);
        if (stored === '1') setDismissed(true);
      }
    } catch (err) {
      // Silent fail — banner stays hidden.
      setData(null);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleReshare = () => {
    if (!waLink) return;
    const base = process.env.REACT_APP_BACKEND_URL || '';
    const pngUrl = `${base}/api/public/signup-qr.png?role=student`;
    const msg = `Quick reminder — register your child with our academy in 2 taps:\n${waLink}\n\nOr scan our QR: ${pngUrl}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank', 'noopener');
  };

  const handleDismiss = () => {
    setDismissed(true);
    if (data?.week_tag && typeof window !== 'undefined') {
      window.localStorage.setItem(DISMISS_KEY_PREFIX + data.week_tag, '1');
    }
  };

  if (!data?.should_show || dismissed || !waLink) {
    return null;
  }

  return (
    <div
      className="rounded-lg border border-amber-200 bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50 p-3 flex items-center gap-3"
      data-testid="signup-nudge-banner"
    >
      <div className="shrink-0 w-9 h-9 rounded-full bg-amber-100 border border-amber-300 flex items-center justify-center">
        <Megaphone className="h-4 w-4 text-amber-700" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-amber-900 leading-tight" data-testid="signup-nudge-title">
          No new self-signups this week
        </p>
        <p className="text-xs text-amber-800/80 leading-snug">
          Re-share your signup QR in the parents WhatsApp group to keep the funnel warm.
        </p>
      </div>
      <Button
        size="sm"
        onClick={handleReshare}
        data-testid="signup-nudge-reshare"
        className="bg-amber-600 hover:bg-amber-700 text-xs gap-1.5 shrink-0"
      >
        <MessageCircle className="h-3.5 w-3.5" /> Re-share QR
      </Button>
      <button
        onClick={handleDismiss}
        data-testid="signup-nudge-dismiss"
        aria-label="Dismiss"
        className="shrink-0 p-1 rounded-md hover:bg-amber-100 text-amber-700"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
